/**
 * useTouchGuard — prevents browser default touch gestures on a container element.
 *
 * WHY THIS EXISTS:
 * react-force-graph-2d uses d3-zoom, which registers its touchstart/touchmove
 * listeners WITHOUT { passive: false }. Modern browsers treat touch listeners as
 * passive by default, so d3-zoom's internal preventDefault() calls are silently
 * ignored. The browser then intercepts touch gestures for its own scroll/zoom,
 * breaking both graph panning and node dragging on mobile.
 *
 * HOW IT WORKS:
 * Registers non-passive touchstart/touchmove listeners on the DOCUMENT in the
 * CAPTURE phase. Capture-phase listeners fire BEFORE any element-level handlers,
 * which is critical because d3-drag/d3-zoom call stopImmediatePropagation() in
 * their touch handlers — this prevents events from bubbling to wrapper divs.
 * By using capture phase at the document level, we guarantee our preventDefault()
 * fires before d3 can block propagation.
 *
 * Only touches originating inside the guarded element (the wrapper div) are
 * prevented — other UI elements (header, modals, buttons) are unaffected.
 *
 * IMPORTANT — DO NOT:
 * - Remove or replace this hook (mobile touch breaks immediately)
 * - Use React's onTouchStart/onTouchMove instead (React registers these as
 *   passive, so preventDefault() is ignored — the whole point of this hook)
 * - Move listeners to the wrapper div's bubbling phase (d3 calls
 *   stopImmediatePropagation which prevents events from reaching it)
 * - Add dependencies that cause re-registration (creates brief gaps with no
 *   prevention where touch gestures slip through)
 *
 * The wrapper div must also have style={{ touchAction: 'none' }} as a CSS-level
 * fallback — some browsers (especially Safari) need both the CSS property and
 * the JS preventDefault().
 *
 * @returns {React.RefObject} Ref object — attach to a div wrapping ForceGraph2D.
 *
 * @example
 *   const touchGuardRef = useTouchGuard();
 *   return (
 *     <div ref={touchGuardRef} style={{ touchAction: 'none' }}>
 *       <ForceGraph2D ... />
 *     </div>
 *   );
 */

import { useRef, useEffect } from 'react';

export default function useTouchGuard() {
  const elRef = useRef(null);

  useEffect(() => {
    const el = elRef.current;
    if (!el) return;

    // Only prevent default for touches on the graph canvas area.
    // Touches on other UI (header, modals, buttons) pass through normally.
    const prevent = (e) => {
      if (el.contains(e.target)) {
        e.preventDefault();
      }
    };

    // Document-level, capture phase: fires BEFORE any element-level handlers.
    // This is essential because d3-drag calls stopImmediatePropagation() in
    // its touchstarted handler, which would prevent a wrapper-level bubbling
    // handler from ever receiving the event.
    document.addEventListener('touchstart', prevent, { passive: false, capture: true });
    document.addEventListener('touchmove', prevent, { passive: false, capture: true });

    return () => {
      document.removeEventListener('touchstart', prevent, { capture: true });
      document.removeEventListener('touchmove', prevent, { capture: true });
    };
  }, []);

  return elRef;
}
